import React, { useCallback, useEffect, useState } from 'react';
import {
  Autocomplete,
  CircularProgress,
  TextField,
  TextFieldProps,
} from '@mui/material';

import { fetchWrapper } from '@/lib/fetch-wrapper';
import { urls } from '@/urls';

export interface FilterUser {
  id: number;
  firstName?: string;
  lastName?: string;
  username: string;
}

export function formatUserLabel(user: FilterUser): string {
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  if (name && user.username) {
    return `${name} (${user.username})`;
  }
  return user.username || String(user.id);
}

interface UserSelectProps {
  value: FilterUser | null;
  onChange: (user: FilterUser | null) => void;
  label?: string;
  disabled?: boolean;
  testId?: string;
  size?: TextFieldProps['size'];
  helperText?: string;
}

function UserSelect({
  value,
  onChange,
  label = 'User',
  disabled = false,
  testId,
  size = 'small',
  helperText,
}: UserSelectProps) {
  const [users, setUsers] = useState<FilterUser[]>([]);
  const [loading, setLoading] = useState(true);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchWrapper.get(urls.DevOps.getFilterUsers);
      setUsers(Array.isArray(data) ? data : []);
    } catch {
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers().catch(() => undefined);
  }, [loadUsers]);

  return (
    <Autocomplete
      disabled={disabled}
      loading={loading}
      options={users}
      value={value}
      onChange={(_e, newValue) => onChange(newValue)}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      getOptionLabel={(option) => formatUserLabel(option)}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          size={size}
          label={label}
          helperText={helperText}
          inputProps={{
            ...params.inputProps,
            'data-testid': testId,
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={18} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

export default UserSelect;
